import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, Save, Users, X, Search } from 'lucide-react';
import api from '../../../api/config';

// Paleta de colores para las barras del timeline
const BAR_COLORS = [
  { bg: 'bg-blue-500',   hex: '#3b82f6' },
  { bg: 'bg-amber-400',  hex: '#fbbf24' },
  { bg: 'bg-emerald-500',hex: '#10b981' },
  { bg: 'bg-violet-500', hex: '#8b5cf6' },
  { bg: 'bg-rose-500',   hex: '#f43f5e' },
  { bg: 'bg-cyan-500',   hex: '#06b6d4' },
  { bg: 'bg-orange-500', hex: '#f97316' },
  { bg: 'bg-teal-500',   hex: '#14b8a6' },
];

const toMin = (t) => {
  const [h, m] = (t || '00:00').split(':').map(Number);
  return h * 60 + m;
};

const DIAS = [
  { key: 'lunes',     label: 'Lun' },
  { key: 'martes',    label: 'Mar' },
  { key: 'miercoles', label: 'Mié' },
  { key: 'jueves',    label: 'Jue' },
  { key: 'viernes',   label: 'Vie' },
  { key: 'sabado',    label: 'Sáb' },
  { key: 'domingo',   label: 'Dom' },
];

const EMPTY_HORARIOS = { lunes: null, martes: null, miercoles: null, jueves: null, viernes: null, sabado: null, domingo: null };

export default function ShiftManager() {
  const [shifts,    setShifts]    = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);

  const [form, setForm] = useState({ nombre: '', ciclo_semanas: 1, horarios_semana: EMPTY_HORARIOS, activo: true });

  // Modal empleados
  const [showModal,          setShowModal]          = useState(false);
  const [employees,          setEmployees]          = useState([]);
  const [assignedEmployees,  setAssignedEmployees]  = useState([]);
  const [selectedEmployees,  setSelectedEmployees]  = useState([]);
  const [departments,        setDepartments]        = useState([]);
  const [selectedDept,       setSelectedDept]       = useState('');
  const [assignMode,         setAssignMode]         = useState('individual');
  const [searchTerm,         setSearchTerm]         = useState('');
  const [loadingModal,       setLoadingModal]       = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sr, sc] = await Promise.all([api.get('/shifts'), api.get('/schedules')]);
      setShifts(sr.data.shifts || []);
      setSchedules(sc.data.schedules || []);
    } finally { setLoading(false); }
  };

  const handleSelect = (s) => {
    setSelected(s);
    setForm({
      nombre: s.nombre,
      ciclo_semanas: s.ciclo_semanas || 1,
      horarios_semana: s.horarios_semana || EMPTY_HORARIOS,
      activo: s.activo ?? true,
    });
  };

  const handleNew = () => {
    setSelected(null);
    setForm({ nombre: '', ciclo_semanas: 1, horarios_semana: EMPTY_HORARIOS, activo: true });
  };

  const setDia = (dia, val) => setForm(prev => ({
    ...prev, horarios_semana: { ...prev.horarios_semana, [dia]: val || null }
  }));

  const handleSave = async () => {
    if (!form.nombre.trim()) return alert('Ingresa un nombre para el turno.');
    try {
      setSaving(true);
      if (selected) {
        const r = await api.put(`/shifts/${selected.id}`, form);
        setSelected(r.data.shift);
      } else {
        const r = await api.post('/shifts', form);
        setSelected(r.data.shift);
      }
      await loadData();
    } catch (e) {
      alert('Error al guardar: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este turno?')) return;
    try {
      await api.delete(`/shifts/${id}`);
      await loadData();
      if (selected?.id === id) handleNew();
    } catch (e) { alert('Error al eliminar'); }
  };

  const getScheduleById = (id) => schedules.find(s => s.id === id);
  const getColor = (scheduleId) => {
    if (!scheduleId) return null;
    const idx = schedules.findIndex(s => s.id === scheduleId);
    return BAR_COLORS[idx % BAR_COLORS.length];
  };

  // ── Modal empleados ─────────────────────────────────
  const openModal = async () => {
    if (!selected) return alert('Guarda el turno primero.');
    setShowModal(true);
    setLoadingModal(true);
    try {
      const [empRes, deptRes, assignedRes] = await Promise.all([
        api.get('/employees'),
        api.get('/departments'),
        api.get(`/shifts/${selected.id}/employees`),
      ]);
      setEmployees(empRes.data.employees || []);
      setDepartments(deptRes.data || []);
      setAssignedEmployees(assignedRes.data.employees || []);
    } catch (e) { alert('Error al cargar empleados'); }
    finally { setLoadingModal(false); }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEmployees([]);
    setSearchTerm('');
    setAssignMode('individual');
    setSelectedDept('');
  };

  const handleAssign = async () => {
    if (!selectedEmployees.length) return alert('Selecciona al menos un empleado.');
    try {
      await api.post(`/shifts/${selected.id}/assign-employees`, {
        employee_ids: selectedEmployees,
        fecha_inicio: new Date().toISOString().split('T')[0],
        es_permanente: true,
      });
      const r = await api.get(`/shifts/${selected.id}/employees`);
      setAssignedEmployees(r.data.employees || []);
      setSelectedEmployees([]);
    } catch (e) { alert('Error al asignar'); }
  };

  const handleAssignDept = async () => {
    if (!selectedDept) return alert('Selecciona un departamento.');
    const dept = departments.find(d => d.id === selectedDept);
    if (!confirm(`¿Asignar todos los activos de "${dept?.nombre}" a este turno?`)) return;
    try {
      const r = await api.post(`/shifts/${selected.id}/assign-department`, {
        department_id: selectedDept,
        fecha_inicio: new Date().toISOString().split('T')[0],
        es_permanente: true,
      });
      alert(r.data.message);
      closeModal();
    } catch (e) { alert(e.response?.data?.error || 'Error al asignar'); }
  };

  const handleRemove = async (employeeId) => {
    if (!confirm('¿Quitar este empleado del turno?')) return;
    try {
      await api.delete(`/shifts/${selected.id}/employees/${employeeId}`);
      const r = await api.get(`/shifts/${selected.id}/employees`);
      setAssignedEmployees(r.data.employees || []);
    } catch (e) { alert('Error al remover'); }
  };

  const filteredEmployees = employees.filter(emp => {
    const assigned = assignedEmployees.some(ae => ae.employee_id === emp.id);
    const match = !searchTerm ||
      emp.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (emp.employee_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    return !assigned && match;
  });

  // Leyenda única de horarios usados en el turno
  const usedScheduleIds = [...new Set(Object.values(form.horarios_semana).filter(Boolean))];

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">

        {/* ── Lista ─────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-800">Turnos</span>
            <button
              onClick={handleNew}
              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors"
            >
              <Plus size={13} /> Nuevo
            </button>
          </div>

          <div className="divide-y divide-gray-50 max-h-[580px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" />
              </div>
            ) : shifts.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Calendar size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No hay turnos</p>
              </div>
            ) : (
              shifts.map(s => (
                <button
                  key={s.id}
                  onClick={() => handleSelect(s)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    selected?.id === s.id ? 'bg-indigo-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    selected?.id === s.id ? 'bg-indigo-100' : 'bg-gray-100'
                  }`}>
                    <Calendar size={14} className={selected?.id === s.id ? 'text-indigo-600' : 'text-gray-500'} />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${selected?.id === s.id ? 'text-indigo-700' : 'text-gray-800'}`}>
                      {s.nombre}
                    </p>
                    <p className="text-xs text-gray-400">Ciclo: {s.ciclo_semanas} sem.</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ── Detalle / Formulario ───────────────────── */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 shadow-sm">

          {/* Form header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {selected ? 'Editar turno' : 'Nuevo turno'}
              </h3>
              {selected && <p className="text-xs text-gray-400 mt-0.5">{selected.nombre}</p>}
            </div>
            <div className="flex gap-2">
              {selected && (
                <>
                  <button
                    onClick={openModal}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
                  >
                    <Users size={13} /> Empleados
                  </button>
                  <button
                    onClick={() => handleDelete(selected.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={13} /> Eliminar
                  </button>
                </>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {saving ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" /> : <Save size={13} />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Nombre + ciclo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del turno</label>
                <input
                  type="text" value={form.nombre}
                  onChange={e => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Ej: Matutino 9-6"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400 bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Ciclo (semanas)</label>
                <select
                  value={form.ciclo_semanas}
                  onChange={e => setForm({ ...form, ciclo_semanas: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400 bg-white"
                >
                  {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} semana{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>
            </div>

            {/* Timeline */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Horarios por día</p>
                {/* Leyenda */}
                <div className="flex flex-wrap gap-2">
                  {usedScheduleIds.map(id => {
                    const sch = getScheduleById(id);
                    const color = getColor(id);
                    if (!sch || !color) return null;
                    return (
                      <div key={id} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <div className={`w-3 h-3 rounded-sm ${color.bg}`} />
                        {sch.nombre}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                {/* Header de horas */}
                <div className="flex items-center mb-2">
                  <div className="w-10 flex-shrink-0" />
                  <div className="flex-1 relative h-4">
                    {Array.from({ length: 13 }, (_, i) => (
                      <span
                        key={i}
                        className="absolute text-xs text-gray-400 -translate-x-1/2 select-none"
                        style={{ left: `${(i * 2 / 24) * 100}%` }}
                      >
                        {String(i * 2).padStart(2, '0')}:00
                      </span>
                    ))}
                  </div>
                  <div className="ml-3 w-52 flex-shrink-0" />
                </div>

                {/* Filas de días */}
                <div className="space-y-2">
                  {DIAS.map(dia => {
                    const scheduleId = form.horarios_semana[dia.key];
                    const schedule   = scheduleId ? getScheduleById(scheduleId) : null;
                    const color      = getColor(scheduleId);

                    return (
                      <div key={dia.key} className="flex items-center gap-3">
                        {/* Etiqueta día */}
                        <div className="w-10 flex-shrink-0 text-xs font-medium text-gray-500 text-right">
                          {dia.label}
                        </div>

                        {/* Barra */}
                        <div className="flex-1 relative">
                          {/* Grid lines */}
                          <div className="absolute inset-0 flex pointer-events-none">
                            {Array.from({ length: 13 }, (_, i) => (
                              <div
                                key={i}
                                className="absolute top-0 bottom-0 border-l border-gray-200"
                                style={{ left: `${(i * 2 / 24) * 100}%` }}
                              />
                            ))}
                          </div>
                          <div className="h-9 bg-white border border-gray-200 rounded-lg relative overflow-hidden">
                            {schedule && color && (
                              <div
                                className={`absolute h-full ${color.bg} rounded flex items-center justify-center`}
                                style={{
                                  left:  `${toMin(schedule.hora_inicio) / 14.4}%`,
                                  width: `${(toMin(schedule.hora_fin) - toMin(schedule.hora_inicio)) / 14.4}%`,
                                }}
                              >
                                <span className="text-white text-xs font-semibold px-1 truncate">
                                  {schedule.nombre}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Selector */}
                        <div className="ml-3 w-52 flex-shrink-0 flex items-center gap-1.5">
                          <select
                            value={scheduleId || ''}
                            onChange={e => setDia(dia.key, e.target.value)}
                            className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-indigo-400"
                          >
                            <option value="">Sin horario</option>
                            {schedules.map(s => (
                              <option key={s.id} value={s.id}>{s.nombre}</option>
                            ))}
                          </select>
                          {scheduleId && (
                            <button
                              onClick={() => setDia(dia.key, null)}
                              className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                            >
                              <X size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Modal Empleados ──────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            {/* Header modal */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-bold text-gray-900">Empleados del turno</h2>
                <p className="text-xs text-gray-400 mt-0.5">{selected?.nombre}</p>
              </div>
              <button onClick={closeModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {loadingModal ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                </div>
              ) : (
                <>
                  {/* Asignados */}
                  {assignedEmployees.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                        Asignados ({assignedEmployees.length})
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {assignedEmployees.map(emp => (
                          <div key={emp.employee_id} className="flex items-center gap-3 px-3 py-2.5 bg-green-50 border border-green-100 rounded-xl">
                            <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center text-green-700 text-sm font-semibold flex-shrink-0">
                              {emp.empleado_nombre?.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{emp.empleado_nombre}</p>
                              <p className="text-xs text-gray-500 truncate">{emp.departamento_nombre || '—'}</p>
                            </div>
                            <button onClick={() => handleRemove(emp.employee_id)}
                              className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors flex-shrink-0">
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tabs modo */}
                  <div>
                    <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit mb-4">
                      {['individual', 'department'].map(m => (
                        <button
                          key={m}
                          onClick={() => setAssignMode(m)}
                          className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            assignMode === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {m === 'individual' ? 'Individual' : 'Por departamento'}
                        </button>
                      ))}
                    </div>

                    {assignMode === 'individual' && (
                      <>
                        <div className="relative mb-3">
                          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text" value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Buscar empleado…"
                            className="w-full pl-8 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400"
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                          {filteredEmployees.length === 0 ? (
                            <p className="col-span-2 text-center py-8 text-sm text-gray-400">Sin empleados disponibles</p>
                          ) : filteredEmployees.map(emp => (
                            <div
                              key={emp.id}
                              onClick={() => setSelectedEmployees(prev =>
                                prev.includes(emp.id) ? prev.filter(i => i !== emp.id) : [...prev, emp.id]
                              )}
                              className={`flex items-center gap-3 px-3 py-2.5 border rounded-xl cursor-pointer transition-colors ${
                                selectedEmployees.includes(emp.id)
                                  ? 'bg-indigo-50 border-indigo-300'
                                  : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <input type="checkbox" readOnly checked={selectedEmployees.includes(emp.id)}
                                className="w-4 h-4 text-indigo-600 rounded border-gray-300 pointer-events-none" />
                              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 text-sm font-semibold flex-shrink-0">
                                {emp.nombre?.charAt(0)}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">{emp.nombre} {emp.apellido}</p>
                                <p className="text-xs text-gray-400">{emp.employee_id}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {assignMode === 'department' && (
                      <div className="space-y-3">
                        <select
                          value={selectedDept}
                          onChange={e => setSelectedDept(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400 bg-white"
                        >
                          <option value="">— Selecciona un departamento —</option>
                          {departments.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                        </select>
                        {selectedDept && (
                          <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                            Todos los empleados activos de este departamento serán asignados a este turno.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer modal */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <p className="text-xs text-gray-500">
                {assignMode === 'individual'
                  ? `${selectedEmployees.length} seleccionado(s)`
                  : selectedDept ? departments.find(d => d.id === selectedDept)?.nombre : 'Selecciona un departamento'}
              </p>
              <div className="flex gap-2">
                <button onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                  Cancelar
                </button>
                {assignMode === 'individual' ? (
                  <button
                    onClick={handleAssign}
                    disabled={!selectedEmployees.length}
                    className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    Asignar {selectedEmployees.length > 0 && `(${selectedEmployees.length})`}
                  </button>
                ) : (
                  <button
                    onClick={handleAssignDept}
                    disabled={!selectedDept}
                    className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    Asignar departamento
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
